<template>
  <div
    :style="[sizeStyles]"
    :class="{ 'percentage-doughnut__tooltip': tooltip }"
    class="percentage-doughnut"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      :viewBox="`${-size / 2} ${-size / 2} ${size} ${size}`"
    >
      <circle
        cx="0"
        cy="0"
        :r="size /2"
        class="background"
      />

      <circle
        :style="[emptyColorData.colorStyles]"
        :class="[emptyColorData.colorClasses]"
        cx="0"
        cy="0"
        :r="arcRadius"
        :stroke-width="strokeWidth"
        class="percentage-doughnut__underlay"
      />

      <path
        :style="[colorData.colorStyles]"
        :class="[colorData.colorClasses]"
        :d="`M 0 ${-arcRadius} A ${arcRadius} ${arcRadius} 0 ${percentageData.largeArcFlag ? '1' : '0'} ${percentageData.sweepFlag ? '1' : '0'} ${percentageData.pathCoord.x} ${percentageData.pathCoord.y}`"
        :stroke-width="strokeWidth"
        class="percentage-doughnut__overlay"
        @mouseover="$vuetify.breakpoint.mobile ? null : percentageTooltipVisible = true"
        @mouseleave="$vuetify.breakpoint.mobile ? null : percentageTooltipVisible = false"
        @click="$vuetify.breakpoint.mobile ? percentageTooltipVisible = true : null"
      />

      <path
        v-if="data.others != null"
        :d="`M 0 ${-arcRadius} A ${arcRadius} ${arcRadius} 0 ${restData.largeArcFlag ? '1' : '0'} ${restData.sweepFlag ? '1' : '0'} ${restData.pathCoord.x} ${restData.pathCoord.y}`"
        :stroke-width="strokeWidth"
        class="percentage-doughnut__rest-path"
        @mouseover="$vuetify.breakpoint.mobile ? null : restTooltipVisible = true"
        @mouseleave="$vuetify.breakpoint.mobile ? null : restTooltipVisible = false"
        @click="$vuetify.breakpoint.mobile ? restTooltipVisible = true : null"
      />

      <circle
        :id="`percentage-tooltip-activator-${id}`"
        r="0.00001"
        :cx="percentageData.activatorCoord.x"
        :cy="percentageData.activatorCoord.y"
        class="tooltip-activator"
      />

      <circle
        v-if="data.others != null"
        :id="`rest-tooltip-activator-${id}`"
        r="0.00001"
        :cx="restData.activatorCoord.x"
        :cy="restData.activatorCoord.y"
        class="tooltip-activator"
      />

      <foreignObject v-if="tooltip">
        <DoughnutResume
          v-model="percentageTooltipVisible"
          :activator="`#percentage-tooltip-activator-${id}`"
          :item="data"
          :percentage="data.percentage / 2"
          :radius="radius"
        />

        <DoughnutResume
          v-if="data.others != null"
          v-model="restTooltipVisible"
          :activator="`#rest-tooltip-activator-${id}`"
          :item="{ value: data.others.value, label: data.others.label, unit: data.unit, precision: data.precision }"
          :percentage="restData.percentage / 2"
          :radius="radius"
        />
      </foreignObject>

      <g class="inset">
        <circle
          cx="0"
          cy="0"
          :r="insetRadius"
          class="inset-circle"
        />
      </g>
    </svg>

    <div
      :style="[slotSizeStyles]"
      class="slot"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import { getTextColorData } from './colorUtils'
import DoughnutResume from './doughnut-resume.vue'

export default {
  name: 'PercentageDoughnut',

  components: {
    DoughnutResume
  },

  props: {
    data: {
      type: Object,
      required: true
    },
    size: {
      type: [Number, String],
      default: 200
    },
    width: {
      type: [Number, String],
      default: 20
    },
    positiveColor: {
      type: String,
      default: 'success'
    },
    negativeColor: {
      type: String,
      default: 'warning'
    },
    emptyColor: {
      type: String,
      default: 'grey lighten-4'
    },
    tooltip: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      id: uuidv4(),

      padding: 12,

      percentageTooltipVisible: false,
      restTooltipVisible: false
    }
  },

  computed: {
    sizeStyles () {
      return {
        width: `${this.size}px`,
        height: `${this.size}px`,
        minWidth: `${this.size}px`,
        minHeight: `${this.size}px`
      }
    },

    radius () {
      return this.size / 2 - Number(this.padding)
    },

    strokeWidth () {
      return Number(this.width)
    },

    arcRadius () {
      return this.radius - this.strokeWidth / 2
    },

    percentageData () {
      return {
        pathCoord: this.getCoord(this.data.percentage, this.arcRadius),
        largeArcFlag: Math.abs(this.data.percentage) > 50,
        sweepFlag: this.data.percentage > 0,
        activatorCoord: this.getCoord(this.data.percentage / 2, this.radius)
      }
    },

    restData () {
      const percentage = (100 - Math.abs(this.data.percentage)) * (this.data.percentage > 0 ? -1 : 1)

      return {
        percentage,
        pathCoord: this.getCoord(percentage, this.arcRadius),
        largeArcFlag: Math.abs(percentage) > 50,
        sweepFlag: percentage > 0,
        activatorCoord: this.getCoord(percentage / 2, this.radius)
      }
    },

    emptyColorData () {
      return getTextColorData(this.emptyColor)
    },

    colorData () {
      return getTextColorData(
        this.data.percentage >= 0 ? this.positiveColor : this.negativeColor
      )
    },

    insetRadius () {
      return this.radius - this.strokeWidth
    },

    slotSizeStyles () {
      return {
        width: `${this.insetRadius * 2}px`,
        height: `${this.insetRadius * 2}px`
      }
    }
  },

  methods: {
    getCoord (value, radius) {
      const anglePercentage = (360 * value) / 100
      const trigoAngle = -(anglePercentage * Math.PI / 180) + Math.PI / 2

      return {
        x: Math.cos(trigoAngle) * radius,
        y: -Math.sin(trigoAngle) * radius
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.percentage-doughnut {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 1000000px;
  box-shadow:
    0 3px 1px -2px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)),
    0 2px 2px 0 var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)),
    0 1px 5px 0 var(--v-shadow-key-ambient-opacity, rgba(0, 0, 0, 0.12));

  > svg {
    width: 100%;
    height: 100%;
    overflow: visible;

    .background {
      fill: #ffffff;
      stroke: none;
    }

    .percentage-doughnut__underlay {
      fill: transparent;
      stroke: currentColor;
      z-index: 1;
    }

    .percentage-doughnut__overlay {
      fill: transparent;
      stroke: currentColor;
    }

    .percentage-doughnut__rest-path {
      fill: transparent;
      stroke: transparent;
    }

    .tooltip-activator {
      fill: transparent;
      stroke: transparent;
    }

    .inset {
      filter: drop-shadow(1px 1px 1px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)));

      .inset-circle {
        overflow: visible;
        fill: #ffffff;
        stroke: none;
      }
    }
  }

  &.percentage-doughnut__tooltip {
    .percentage-doughnut__wrapper {
      > svg {
        .percentage-doughnut__overlay {
          &:hover {
            filter: drop-shadow(
              0px 1px 1px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2))
            );
          }
        }
      }
    }
  }

  .slot {
    position: absolute;
    z-index: 2;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    border-radius: 1000000px;
  }
}
</style>
